import { jsPDF } from 'jspdf';
import { svg2pdf } from 'svg2pdf.js';
import { SHEET } from '@/components/viewer/drawing/constants';

const JP_FONT_NAME = 'NotoSansJP';
const JP_FONT_URL = '/NotoSansJP-Regular.ttf';

export type ExportOptions = {
  title?: string;
  author?: string;
  drawingNumberPrefix?: string;
};

/**
 * Load and register Noto Sans JP into jsPDF so Japanese text renders in PDF.
 * The font file is fetched once and cached in a module-level variable.
 */
let fontBase64Cache: string | null = null;

async function registerJapaneseFont(pdf: jsPDF): Promise<void> {
  if (!fontBase64Cache) {
    const resp = await fetch(JP_FONT_URL);
    if (!resp.ok) {
      console.warn('[drawingExport] Failed to load Japanese font, PDF may have garbled text');
      return;
    }
    const buf = await resp.arrayBuffer();
    // Convert to base64 string for jsPDF
    const bytes = new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    fontBase64Cache = btoa(binary);
  }

  const fileName = `${JP_FONT_NAME}-Regular.ttf`;
  pdf.addFileToVFS(fileName, fontBase64Cache);
  pdf.addFont(fileName, JP_FONT_NAME, 'normal');
  // Register the same font file for bold/italic variants so svg2pdf
  // doesn't fall back to Helvetica for fontWeight > 400
  pdf.addFont(fileName, JP_FONT_NAME, 'bold');
  pdf.addFont(fileName, JP_FONT_NAME, 'italic');
  pdf.addFont(fileName, JP_FONT_NAME, 'bolditalic');
}

/**
 * Rewrite font-family attributes in SVG clone so svg2pdf picks up the
 * embedded font instead of falling back to Helvetica.
 *
 * IMPORTANT: We rewrite ALL text elements to use the registered JP font,
 * not just those with specific font-family values. This ensures Japanese
 * characters render correctly in the PDF output.
 */
function rewriteFontsInSvg(svgClone: SVGSVGElement): void {
  // Rewrite ALL text and tspan elements to use the registered font
  // Also normalize font-weight to avoid svg2pdf looking for bold font variants
  const allText = svgClone.querySelectorAll('text, tspan');
  allText.forEach((el) => {
    el.setAttribute('font-family', JP_FONT_NAME);
    // svg2pdf maps fontWeight > 400 to 'bold' style, which needs a separate font file.
    // Since we only have Regular, remove fontWeight so it uses 'normal' for all text.
    if (el.hasAttribute('font-weight')) {
      el.removeAttribute('font-weight');
    }
  });
  // Also rewrite inline styles
  const allElements = svgClone.querySelectorAll('[style]');
  allElements.forEach((el) => {
    const style = el.getAttribute('style') || '';
    if (style.includes('font-family')) {
      el.setAttribute(
        'style',
        style.replace(/font-family\s*:[^;]+/g, `font-family: ${JP_FONT_NAME}`),
      );
    }
  });
}

function buildFilename(opts: ExportOptions | undefined, ext: string, sheetIndex?: number): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');

  if (sheetIndex !== undefined) {
    return `synergy-cad-${date}-sheet${sheetIndex + 1}.${ext}`;
  }
  return `synergy-cad-${date}.${ext}`;
}

function prepareSvgClone(svgElement: SVGSVGElement, sheetW: number, sheetH: number): SVGSVGElement {
  const clone = svgElement.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('viewBox', `0 0 ${sheetW} ${sheetH}`);
  clone.setAttribute('width', `${sheetW}mm`);
  clone.setAttribute('height', `${sheetH}mm`);
  clone.removeAttribute('class');
  clone.removeAttribute('style');
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  const contentGroup = clone.querySelector('g.sheet-content');
  if (contentGroup) {
    contentGroup.removeAttribute('transform');
  }

  return clone;
}

export function exportDrawingSVG(svgElement: SVGSVGElement, opts?: ExportOptions): void {
  const sheetW = SHEET.A3.w;
  const sheetH = SHEET.A3.h;
  const clone = prepareSvgClone(svgElement, sheetW, sheetH);

  const serializer = new XMLSerializer();
  const svgString = '<?xml version="1.0" encoding="UTF-8"?>\n' + serializer.serializeToString(clone);
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  triggerDownload(blob, buildFilename(opts, 'svg'));
}

/**
 * Export multiple sheets as a .zip of SVG files via JSZip (lazy-loaded).
 */
export async function exportDrawingSVGZip(
  svgElements: SVGSVGElement[],
  sheetSize: 'A3' | 'A4' | 'A2' | 'A1' = 'A3',
  opts?: ExportOptions,
): Promise<void> {
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  const sheet = SHEET[sheetSize];
  const serializer = new XMLSerializer();

  svgElements.forEach((svg, i) => {
    const clone = prepareSvgClone(svg, sheet.w, sheet.h);
    const svgString = '<?xml version="1.0" encoding="UTF-8"?>\n' + serializer.serializeToString(clone);
    zip.file(`sheet-${i + 1}.svg`, svgString);
  });

  const content = await zip.generateAsync({ type: 'blob' });
  const title = opts?.title?.replace(/[/\\?%*:|"<>]/g, '_') ?? 'drawing';
  triggerDownload(content, `${opts?.drawingNumberPrefix ?? ''}${title}.zip`);
}

export async function exportDrawingPDF(
  svgElements: SVGSVGElement | SVGSVGElement[],
  sheetSize: 'A3' | 'A4' | 'A2' | 'A1' = 'A3',
  opts?: ExportOptions,
): Promise<void> {
  const sheet = SHEET[sheetSize];
  const svgs = Array.isArray(svgElements) ? svgElements : [svgElements];

  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: [sheet.w, sheet.h],
  });

  // PDF metadata
  pdf.setProperties({
    title: opts?.title ?? 'Drawing',
    author: opts?.author ?? '',
    creator: 'AgentCAD',
    subject: 'Technical Drawing',
  });

  // Embed Japanese font
  await registerJapaneseFont(pdf);
  pdf.setFont(JP_FONT_NAME);

  for (let i = 0; i < svgs.length; i++) {
    if (i > 0) pdf.addPage([sheet.w, sheet.h], 'landscape');

    const clone = svgs[i].cloneNode(true) as SVGSVGElement;
    clone.setAttribute('viewBox', `0 0 ${sheet.w} ${sheet.h}`);
    clone.setAttribute('width', `${sheet.w}`);
    clone.setAttribute('height', `${sheet.h}`);

    const contentGroup = clone.querySelector('g.sheet-content');
    if (contentGroup) {
      contentGroup.removeAttribute('transform');
    }

    rewriteFontsInSvg(clone);

    clone.style.position = 'absolute';
    clone.style.left = '-9999px';
    document.body.appendChild(clone);

    try {
      await svg2pdf(clone, pdf, {
        x: 0,
        y: 0,
        width: sheet.w,
        height: sheet.h,
      });
    } finally {
      document.body.removeChild(clone);
    }
  }

  pdf.save(buildFilename(opts, 'pdf'));
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
